import React, { useState, useEffect, useMemo } from 'react';

interface AnimatedLoadingStateProps {
  text?: string;
}

interface LoadingScene {
  svg: React.ReactNode;
  text: string;
}

const loadingScenes: Omit<LoadingScene, 'svg'>[] = [
  { text: 'Считываем звёздные паттерны...' },
  { text: 'Анализируем положение планет...' },
  { text: 'Изучаем лунные циклы...' },
  { text: 'Соединяем созвездия вашей карты...' },
  { text: 'Расшифровываем послания космоса...' },
  { text: 'Проходим через все знаки зодиака...' },
  { text: 'Рассматриваем сферы вашей жизни...' },
  { text: 'Вычисляем гармоничные аспекты...' },
  { text: 'Определяем вашу космическую суть...' },
  { text: 'Собираем звёздные подсказки...' },
  { text: 'Рисуем вашу натальную карту...' },
  { text: 'Планеты рассказывают о вас...' },
  { text: 'Считываем энергетические паттерны...' },
  { text: 'Ваша уникальная конфигурация формируется...' },
  { text: 'Космос говорит с вами...' },
];

const goldGradient = 'url(#goldGradient)';

const AnimatedLoadingState: React.FC<AnimatedLoadingStateProps> = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const shuffledIndices = useMemo(() => {
    const indices = loadingScenes.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % shuffledIndices.length);
        setIsTransitioning(false);
      }, 400);
    }, 3500);

    return () => clearInterval(interval);
  }, [shuffledIndices.length]);

  const sceneIndex = shuffledIndices[currentIndex];
  const scene = loadingScenes[sceneIndex];

  const renderSceneSVG = () => {
    switch (sceneIndex) {
      case 0: // Мерцающая звезда
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <defs>
              <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f4d03f" />
                <stop offset="50%" stopColor="#d4af37" />
                <stop offset="100%" stopColor="#b8942b" />
              </linearGradient>
            </defs>
            <g style={{ animation: 'loading-twinkle 2s ease-in-out infinite' }}>
              <path d="M60 20 L65 50 L95 55 L65 60 L60 90 L55 60 L25 55 L55 50 Z" fill={goldGradient} />
              <circle cx="60" cy="55" r="4" fill="#fff" opacity="0.8" />
            </g>
            <circle cx="30" cy="30" r="1.5" fill="#d4af37" opacity="0.6" style={{ animation: 'loading-twinkle 1.5s ease-in-out infinite 0.3s' }} />
            <circle cx="90" cy="35" r="1" fill="#d4af37" opacity="0.5" style={{ animation: 'loading-twinkle 1.8s ease-in-out infinite 0.7s' }} />
            <circle cx="85" cy="85" r="1.5" fill="#d4af37" opacity="0.6" style={{ animation: 'loading-twinkle 1.6s ease-in-out infinite 1s' }} />
            <circle cx="35" cy="80" r="1" fill="#d4af37" opacity="0.5" style={{ animation: 'loading-twinkle 1.4s ease-in-out infinite 0.5s' }} />
          </svg>
        );

      case 1: // Вращающаяся планета
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <defs>
              <radialGradient id="planetGradient" cx="40%" cy="40%">
                <stop offset="0%" stopColor="#f4d03f" />
                <stop offset="70%" stopColor="#d4af37" />
                <stop offset="100%" stopColor="#8b6914" />
              </radialGradient>
            </defs>
            <g style={{ animation: 'loading-rotate 8s linear infinite' }}>
              <circle cx="60" cy="60" r="25" fill="url(#planetGradient)" />
              <ellipse cx="60" cy="60" rx="40" ry="8" fill="none" stroke="#d4af37" strokeWidth="1.5" opacity="0.6" />
              <path d="M45 50 Q55 45 65 50" fill="none" stroke="#8b6914" strokeWidth="1" opacity="0.4" />
              <path d="M50 65 Q60 70 70 65" fill="none" stroke="#8b6914" strokeWidth="1" opacity="0.4" />
            </g>
          </svg>
        );

      case 2: // Фазы луны
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <defs>
              <linearGradient id="moonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f4e4a6" />
                <stop offset="100%" stopColor="#d4af37" />
              </linearGradient>
            </defs>
            <g style={{ animation: 'loading-float 3s ease-in-out infinite' }}>
              <circle cx="60" cy="60" r="28" fill="url(#moonGradient)" opacity="0.9" />
              <circle cx="70" cy="60" r="24" fill="rgba(13, 20, 40, 0.9)" />
              <circle cx="60" cy="60" r="28" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.5" />
            </g>
            <circle cx="25" cy="30" r="1" fill="#d4af37" style={{ animation: 'loading-twinkle 2s ease-in-out infinite' }} />
            <circle cx="95" cy="40" r="1.5" fill="#d4af37" style={{ animation: 'loading-twinkle 2.2s ease-in-out infinite 0.5s' }} />
            <circle cx="30" cy="90" r="1" fill="#d4af37" style={{ animation: 'loading-twinkle 1.8s ease-in-out infinite 1s' }} />
          </svg>
        );

      case 3: // Созвездия
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <g style={{ animation: 'loading-fade-in 2s ease-in-out infinite alternate' }}>
              <line x1="30" y1="30" x2="50" y2="45" stroke="#d4af37" strokeWidth="0.8" opacity="0.6" />
              <line x1="50" y1="45" x2="70" y2="35" stroke="#d4af37" strokeWidth="0.8" opacity="0.6" />
              <line x1="70" y1="35" x2="90" y2="50" stroke="#d4af37" strokeWidth="0.8" opacity="0.6" />
              <line x1="50" y1="45" x2="55" y2="70" stroke="#d4af37" strokeWidth="0.8" opacity="0.6" />
              <line x1="55" y1="70" x2="75" y2="85" stroke="#d4af37" strokeWidth="0.8" opacity="0.6" />
              <circle cx="30" cy="30" r="3" fill="#f4d03f" />
              <circle cx="50" cy="45" r="3.5" fill="#f4d03f" />
              <circle cx="70" cy="35" r="3" fill="#f4d03f" />
              <circle cx="90" cy="50" r="2.5" fill="#f4d03f" />
              <circle cx="55" cy="70" r="3" fill="#f4d03f" />
              <circle cx="75" cy="85" r="3.5" fill="#f4d03f" />
            </g>
          </svg>
        );

      case 4: // Хрустальный шар
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <defs>
              <radialGradient id="crystalGradient" cx="35%" cy="35%">
                <stop offset="0%" stopColor="rgba(244, 228, 166, 0.8)" />
                <stop offset="50%" stopColor="rgba(212, 175, 55, 0.4)" />
                <stop offset="100%" stopColor="rgba(13, 20, 40, 0.6)" />
              </radialGradient>
            </defs>
            <g style={{ animation: 'loading-breathe 3s ease-in-out infinite' }}>
              <circle cx="60" cy="55" r="30" fill="url(#crystalGradient)" />
              <circle cx="60" cy="55" r="30" fill="none" stroke="#d4af37" strokeWidth="1" opacity="0.5" />
              <ellipse cx="60" cy="90" rx="25" ry="5" fill="rgba(212, 175, 55, 0.2)" />
              <path d="M50 45 Q55 40 60 45" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
            </g>
            <circle cx="50" cy="50" r="1" fill="#fff" opacity="0.6" style={{ animation: 'loading-twinkle 1.5s ease-in-out infinite' }} />
            <circle cx="68" cy="58" r="1.5" fill="#fff" opacity="0.5" style={{ animation: 'loading-twinkle 1.8s ease-in-out infinite 0.5s' }} />
          </svg>
        );

      case 5: // Знаки зодиака
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <g style={{ animation: 'loading-rotate 20s linear infinite' }}>
              <circle cx="60" cy="60" r="35" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.3" />
              {['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'].map((sign, i) => {
                const angle = (i * 30 - 90) * Math.PI / 180;
                const x = 60 + 35 * Math.cos(angle);
                const y = 60 + 35 * Math.sin(angle);
                return (
                  <text key={i} x={x} y={y} fill="#d4af37" fontSize="10" textAnchor="middle" dominantBaseline="middle" opacity="0.8">
                    {sign}
                  </text>
                );
              })}
            </g>
            <circle cx="60" cy="60" r="5" fill="#f4d03f" style={{ animation: 'loading-pulse 2s ease-in-out infinite' }} />
          </svg>
        );

      case 6: // Дома натальной карты
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <g style={{ animation: 'loading-rotate-slow 30s linear infinite' }}>
              <circle cx="60" cy="60" r="40" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.4" />
              <circle cx="60" cy="60" r="28" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.3" />
              {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => {
                const rad = angle * Math.PI / 180;
                const x1 = 60 + 28 * Math.cos(rad);
                const y1 = 60 + 28 * Math.sin(rad);
                const x2 = 60 + 40 * Math.cos(rad);
                const y2 = 60 + 40 * Math.sin(rad);
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#d4af37" strokeWidth="0.5" opacity="0.5" />;
              })}
            </g>
            <circle cx="60" cy="60" r="3" fill="#f4d03f" />
          </svg>
        );

      case 7: // Аспекты (линии между планетами)
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <defs>
              <linearGradient id="aspectGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#d4af37" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#f4d03f" stopOpacity="0.4" />
              </linearGradient>
            </defs>
            <g style={{ animation: 'loading-fade-in 2.5s ease-in-out infinite alternate' }}>
              <line x1="35" y1="40" x2="85" y2="40" stroke="url(#aspectGradient)" strokeWidth="1" />
              <line x1="35" y1="40" x2="60" y2="85" stroke="url(#aspectGradient)" strokeWidth="1" />
              <line x1="85" y1="40" x2="60" y2="85" stroke="url(#aspectGradient)" strokeWidth="1" />
              <circle cx="35" cy="40" r="6" fill="#d4af37" />
              <circle cx="85" cy="40" r="5" fill="#f4d03f" />
              <circle cx="60" cy="85" r="5.5" fill="#d4af37" />
              <text x="35" y="43" fill="#0A0814" fontSize="7" textAnchor="middle" fontWeight="bold">☉</text>
              <text x="85" y="43" fill="#0A0814" fontSize="7" textAnchor="middle" fontWeight="bold">♃</text>
              <text x="60" y="88" fill="#0A0814" fontSize="7" textAnchor="middle" fontWeight="bold">♄</text>
            </g>
          </svg>
        );

      case 8: // Солнце в центре
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <defs>
              <radialGradient id="sunGradient" cx="50%" cy="50%">
                <stop offset="0%" stopColor="#fff5d4" />
                <stop offset="40%" stopColor="#f4d03f" />
                <stop offset="100%" stopColor="#d4af37" />
              </radialGradient>
            </defs>
            <g style={{ animation: 'loading-rotate 15s linear infinite' }}>
              {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
                const rad = angle * Math.PI / 180;
                const x2 = 60 + 35 * Math.cos(rad);
                const y2 = 60 + 35 * Math.sin(rad);
                return <line key={i} x1="60" y1="60" x2={x2} y2={y2} stroke="#d4af37" strokeWidth="1.5" opacity="0.5" />;
              })}
            </g>
            <circle cx="60" cy="60" r="18" fill="url(#sunGradient)" style={{ animation: 'loading-breathe 2s ease-in-out infinite' }} />
            <circle cx="60" cy="60" r="18" fill="none" stroke="#f4d03f" strokeWidth="1" opacity="0.6" />
          </svg>
        );

      case 9: // Падающие звёзды
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <g>
              <g style={{ animation: 'loading-shoot1 2s ease-in infinite' }}>
                <line x1="20" y1="20" x2="35" y2="35" stroke="#f4d03f" strokeWidth="1.5" />
                <circle cx="20" cy="20" r="2" fill="#fff" />
              </g>
              <g style={{ animation: 'loading-shoot2 2.5s ease-in infinite 0.8s' }}>
                <line x1="80" y1="15" x2="65" y2="40" stroke="#f4d03f" strokeWidth="1.5" />
                <circle cx="80" cy="15" r="2" fill="#fff" />
              </g>
              <g style={{ animation: 'loading-shoot3 2.2s ease-in infinite 1.5s' }}>
                <line x1="100" y1="50" x2="75" y2="70" stroke="#f4d03f" strokeWidth="1.5" />
                <circle cx="100" cy="50" r="2" fill="#fff" />
              </g>
            </g>
            <circle cx="40" cy="80" r="1" fill="#d4af37" opacity="0.5" style={{ animation: 'loading-twinkle 1.5s ease-in-out infinite' }} />
            <circle cx="70" cy="95" r="1.5" fill="#d4af37" opacity="0.6" style={{ animation: 'loading-twinkle 1.8s ease-in-out infinite 0.5s' }} />
          </svg>
        );

      case 10: // Золотые линии натальной карты
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <g style={{ animation: 'loading-draw 3s ease-in-out infinite' }}>
              <circle cx="60" cy="60" r="40" fill="none" stroke="#d4af37" strokeWidth="1" strokeDasharray="251" strokeDashoffset="251" />
              <circle cx="60" cy="60" r="28" fill="none" stroke="#d4af37" strokeWidth="0.5" strokeDasharray="176" strokeDashoffset="176" style={{ animationDelay: '0.5s' }} />
              <line x1="60" y1="20" x2="60" y2="100" stroke="#d4af37" strokeWidth="0.5" opacity="0.4" strokeDasharray="80" strokeDashoffset="80" />
              <line x1="20" y1="60" x2="100" y2="60" stroke="#d4af37" strokeWidth="0.5" opacity="0.4" strokeDasharray="80" strokeDashoffset="80" style={{ animationDelay: '0.3s' }} />
            </g>
          </svg>
        );

      case 11: // Парящие символы планет
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <text x="30" y="40" fill="#d4af37" fontSize="16" style={{ animation: 'loading-float 3s ease-in-out infinite' }}>☉</text>
            <text x="75" y="35" fill="#f4d03f" fontSize="14" style={{ animation: 'loading-float 3.5s ease-in-out infinite 0.5s' }}>☽</text>
            <text x="50" y="70" fill="#d4af37" fontSize="13" style={{ animation: 'loading-float 2.8s ease-in-out infinite 1s' }}>♂</text>
            <text x="85" y="75" fill="#f4d03f" fontSize="15" style={{ animation: 'loading-float 3.2s ease-in-out infinite 1.5s' }}>♀</text>
            <text x="25" y="90" fill="#d4af37" fontSize="14" style={{ animation: 'loading-float 3s ease-in-out infinite 0.8s' }}>♃</text>
            <text x="65" y="95" fill="#f4d03f" fontSize="13" style={{ animation: 'loading-float 2.5s ease-in-out infinite 1.2s' }}>♄</text>
          </svg>
        );

      case 12: // Градиентное пятно
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <defs>
              <radialGradient id="energyGradient" cx="50%" cy="50%">
                <stop offset="0%" stopColor="#f4d03f" stopOpacity="0.8">
                  <animate attributeName="stop-color" values="#f4d03f;#d4af37;#4fd1c5;#d4af37;#f4d03f" dur="4s" repeatCount="indefinite" />
                </stop>
                <stop offset="100%" stopColor="#d4af37" stopOpacity="0">
                  <animate attributeName="stop-color" values="#d4af37;#4fd1c5;#f4d03f;#4fd1c5;#d4af37" dur="4s" repeatCount="indefinite" />
                </stop>
              </radialGradient>
            </defs>
            <circle cx="60" cy="60" r="35" fill="url(#energyGradient)" style={{ animation: 'loading-breathe 3s ease-in-out infinite' }} />
            <circle cx="60" cy="60" r="20" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.4" style={{ animation: 'loading-rotate 10s linear infinite' }} />
          </svg>
        );

      case 13: // Круг со знаками зодиака (вращающийся)
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            <g style={{ animation: 'loading-rotate 12s linear infinite' }}>
              <circle cx="60" cy="60" r="38" fill="none" stroke="#d4af37" strokeWidth="1" opacity="0.5" />
              {['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'].map((sign, i) => {
                const angle = (i * 30 - 90) * Math.PI / 180;
                const x = 60 + 38 * Math.cos(angle);
                const y = 60 + 38 * Math.sin(angle);
                return (
                  <text key={i} x={x} y={y} fill="#f4d03f" fontSize="9" textAnchor="middle" dominantBaseline="middle">
                    {sign}
                  </text>
                );
              })}
            </g>
            <g style={{ animation: 'loading-rotate-reverse 8s linear infinite' }}>
              <circle cx="60" cy="60" r="25" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.3" strokeDasharray="5 3" />
            </g>
            <circle cx="60" cy="60" r="4" fill="#f4d03f" style={{ animation: 'loading-pulse 1.5s ease-in-out infinite' }} />
          </svg>
        );

      case 14: // Звёздная пыль
        return (
          <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%' }}>
            {Array.from({ length: 20 }).map((_, i) => {
              const cx = 20 + Math.random() * 80;
              const cy = 20 + Math.random() * 80;
              const r = 0.5 + Math.random() * 2;
              const delay = Math.random() * 2;
              const duration = 1.5 + Math.random() * 1.5;
              return (
                <circle
                  key={i}
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={i % 3 === 0 ? '#f4d03f' : '#d4af37'}
                  opacity="0.7"
                  style={{ animation: `loading-twinkle ${duration}s ease-in-out infinite ${delay}s` }}
                />
              );
            })}
            <circle cx="60" cy="60" r="8" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.4" style={{ animation: 'loading-breathe 2s ease-in-out infinite' }} />
          </svg>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '300px',
      padding: '40px 20px',
    }}>
      {/* Glassmorphism карточка */}
      <div style={{
        background: 'rgba(13, 20, 40, 0.85)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.15)',
        borderRadius: '24px',
        boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
        padding: '40px',
        maxWidth: '280px',
        width: '100%',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Золотая линия сверху */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
        }} />

        {/* SVG сцена */}
        <div style={{
          width: '120px',
          height: '120px',
          margin: '0 auto 24px',
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? 'scale(0.9)' : 'scale(1)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
        }}>
          {renderSceneSVG()}
        </div>

        {/* Текст */}
        <p style={{
          background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontSize: '15px',
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          fontWeight: 500,
          letterSpacing: '0.3px',
          lineHeight: '1.5',
          margin: 0,
          minHeight: '48px',
          opacity: isTransitioning ? 0 : 0.9,
          transform: isTransitioning ? 'translateY(5px)' : 'translateY(0)',
          transition: 'opacity 0.4s ease 0.1s, transform 0.4s ease 0.1s',
        }}>
          {scene.text}
        </p>
      </div>

      {/* CSS анимации */}
      <style>{`
        @keyframes loading-twinkle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes loading-rotate {
          to { transform: rotate(360deg); }
        }
        @keyframes loading-rotate-reverse {
          to { transform: rotate(-360deg); }
        }
        @keyframes loading-rotate-slow {
          to { transform: rotate(360deg); }
        }
        @keyframes loading-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes loading-breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes loading-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.95); }
        }
        @keyframes loading-fade-in {
          from { opacity: 0.4; }
          to { opacity: 1; }
        }
        @keyframes loading-draw {
          0% { stroke-dashoffset: 251; }
          50% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -251; }
        }
        @keyframes loading-shoot1 {
          0% { transform: translate(0, 0); opacity: 1; }
          100% { transform: translate(30px, 30px); opacity: 0; }
        }
        @keyframes loading-shoot2 {
          0% { transform: translate(0, 0); opacity: 1; }
          100% { transform: translate(-30px, 40px); opacity: 0; }
        }
        @keyframes loading-shoot3 {
          0% { transform: translate(0, 0); opacity: 1; }
          100% { transform: translate(-40px, 30px); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default AnimatedLoadingState;
